// ===========================================
// CharacterIllustration Utilities
// ===========================================
// Helper functions for character/age group operations

import type { AgeGroup } from './CharacterIllustration';

// Character configurations (colors and names only - no React components)
const characterMeta: Record<AgeGroup, { name: string; bgColor: string }> = {
  PRESCHOOL: { name: 'Alice', bgColor: '#FFB6C1' },
  KIDS: { name: 'Steve', bgColor: '#FFCE00' },
  TEENS: { name: 'Liam', bgColor: '#4580E4' },
  ADULT: { name: 'Floyd', bgColor: '#96DAC9' },
};

/**
 * Get age group from birth date
 */
export function getAgeGroupFromBirthDate(birthDate: Date | string): AgeGroup {
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  if (age < 6) return 'PRESCHOOL';
  if (age < 12) return 'KIDS';
  if (age < 18) return 'TEENS';
  return 'ADULT';
}

/**
 * The character to use when a name maps to nothing. `KIDS` (Steve) is what
 * every student rendered as until 2026-09-08, so an unmapped name changes
 * nothing a teacher has already seen.
 */
const FALLBACK_AGE_GROUP: AgeGroup = 'KIDS';

// Exact matches after normalisation (uppercase, letters only). A school names its
// own age groups in the admin UI — the form even suggests "Pre-school" — so this
// list is synonyms, not a contract. Anything it misses falls through to the
// prefix rule, then to the fallback.
const NAME_TO_AGE_GROUP: Record<string, AgeGroup> = {
  PRESCHOOL: 'PRESCHOOL',
  PRESCHOOLER: 'PRESCHOOL',
  PRESCHOOLERS: 'PRESCHOOL',
  EARLYYEARS: 'PRESCHOOL',
  TODDLER: 'PRESCHOOL',
  TODDLERS: 'PRESCHOOL',
  KINDY: 'PRESCHOOL',
  KINDER: 'PRESCHOOL',
  KINDERGARTEN: 'PRESCHOOL',
  PREK: 'PRESCHOOL',
  PREKINDY: 'PRESCHOOL',
  PREKINDER: 'PRESCHOOL',
  PREKINDERGARTEN: 'PRESCHOOL',
  BABY: 'PRESCHOOL',
  BABIES: 'PRESCHOOL',
  INFANT: 'PRESCHOOL',
  INFANTS: 'PRESCHOOL',
  LITTLEONES: 'PRESCHOOL',
  LITTLIES: 'PRESCHOOL',
  KID: 'KIDS',
  KIDS: 'KIDS',
  CHILD: 'KIDS',
  CHILDREN: 'KIDS',
  JUNIOR: 'KIDS',
  JUNIORS: 'KIDS',
  PRIMARY: 'KIDS',
  TEEN: 'TEENS',
  TEENS: 'TEENS',
  TEENAGER: 'TEENS',
  TEENAGERS: 'TEENS',
  YOUTH: 'TEENS',
  HIGHSCHOOL: 'TEENS',
  // Deliberately NOT mapped: "Senior(s)" — senior students (teens) or senior
  // citizens (adults) depending on the school. It takes the fallback.
  ADULT: 'ADULT',
  ADULTS: 'ADULT',
  GROWNUP: 'ADULT',
  GROWNUPS: 'ADULT',
};

const PREFIX_TO_AGE_GROUP: Array<[string, AgeGroup]> = [
  ['PRESCHOOL', 'PRESCHOOL'],
  ['PREK', 'PRESCHOOL'],
  ['KID', 'KIDS'],
  ['TEEN', 'TEENS'],
  ['ADULT', 'ADULT'],
];

/**
 * Resolve a SCHOOL'S age-group name to one of the four brand characters.
 *
 * `AgeGroup.name` is free text a school types into the admin form ("Kids",
 * "Pre-school", "Teens", "Adults" at MNM). It is NOT the `AgeGroup` enum, and
 * indexing `characterMeta` with `name.toUpperCase()` threw
 * `Cannot read properties of undefined (reading 'name')` for "Pre-school" and
 * "Adults" — taking the whole Teacher Tasks page and the teacher dashboard down
 * for 4 of 6 teachers (2026-09-08). This is the ONLY door from a name to a
 * character; it is total, so no caller can crash on a naming choice.
 */
export function characterAgeGroupFromName(name: string | null | undefined): AgeGroup {
  if (!name) return FALLBACK_AGE_GROUP;
  const upper = name.toUpperCase();
  // 1. The whole name, letters only: "Pre-school" → PRESCHOOL, "Pre School" → PRESCHOOL.
  const key = upper.replace(/[^A-Z]/g, '');
  if (!key) return FALLBACK_AGE_GROUP;
  const exact = NAME_TO_AGE_GROUP[key];
  if (exact) return exact;
  const byPrefix = PREFIX_TO_AGE_GROUP.find(([prefix]) => key.startsWith(prefix));
  if (byPrefix) return byPrefix[1];
  // 2. Word by word, first known word wins: "Babies & Toddlers" → BABIES,
  //    "Adult Learners" → ADULT. A compound name collapses to nothing in step 1.
  for (const word of upper.split(/[^A-Z]+/)) {
    const byWord = word ? NAME_TO_AGE_GROUP[word] : undefined;
    if (byWord) return byWord;
  }
  return FALLBACK_AGE_GROUP;
}

/**
 * Get character color for an age group.
 * Total: an `as AgeGroup` cast that lies at runtime gets the fallback, never a throw.
 */
export function getCharacterColor(ageGroup: AgeGroup): string {
  return (characterMeta[ageGroup] ?? characterMeta[FALLBACK_AGE_GROUP]).bgColor;
}

/**
 * Get character name for an age group.
 * Total: an `as AgeGroup` cast that lies at runtime gets the fallback, never a throw.
 */
export function getCharacterName(ageGroup: AgeGroup): string {
  return (characterMeta[ageGroup] ?? characterMeta[FALLBACK_AGE_GROUP]).name;
}
